import React, { useCallback, useEffect, useRef, useState } from "react";
import { fmtDate } from "../../site/pages/Forum";
import { Link } from "react-router-dom";
import { FW_STATUS_TONE, FwTerms } from "./LaonaFirmware";
import { projectApi, won } from "./LaonaProjects";
import PlanPanel from "./LaonaPlan";

/* =========================================================
   라오나봇 대화창의 [제작] 탭 — 기능 명세 → [내 프로젝트로 저장] → 내 프로젝트에서 주문(부품 + 펌웨어 개발 · 빌드)
   → 입금 확인 → 펌웨어 개발 → 발송.
   저장하면 설계가 잠기고 지금 설계 · 기능 명세로 펌웨어 개발의뢰가 생긴다(app/laona_fw.py). 사이트는 자동 빌드를 하지 않는다 —
   입금이 확인되면 라오나빌드서버가 의뢰를 가져가 AI 로 빌드할지 직접 빌드할지 고른다.
   예전 자동 빌드로 펌웨어가 완성된 프로젝트(BUILT)는 결산 · 결과물 화면을 그대로 보인다.
   (백엔드 app/laona_production.py · app/laona_fw.py)
   ========================================================= */

const STEPS = [
  ["spec", "기능 명세"], ["save", "저장"], ["order", "주문"], ["paid", "입금 확인"], ["dev", "펌웨어 개발"], ["ship", "발송"],
];
const ESP_WEB_TOOLS = "https://unpkg.com/esp-web-tools@10.4.0/dist/web/install-button.js?module";
/* 펌웨어 서명 링크는 24시간 — 받은 지 1시간이 넘으면 다시 받아 온다 */
const LINK_FRESH_MS = 60 * 60 * 1000;
const LOCKED = ["APPROVED", "BUILT", "ORDERED", "PAID", "SHIPPED", "DELIVERED"];
export const isLocked = (status) => LOCKED.includes(status);

function stepIndex(prod) {
  const s = prod.status;
  const fw = prod.fw?.status;
  if (s === "SHIPPED" || s === "DELIVERED") return 6;
  if (fw === "DONE") return 5;
  if (s === "PAID" || fw === "RECEIVED" || fw === "IN_PROGRESS") return 4;
  if (s === "ORDERED") return 3;
  if (s === "APPROVED" || s === "BUILT") return 2; // 저장됨 — 내 프로젝트에서 주문할 차례
  return prod.spec && !prod.spec_stale ? 1 : 0;
}

function Stepper({ prod }) {
  const at = stepIndex(prod);
  return (
    <ol className="lb-mk-steps">
      {STEPS.map(([k, label], i) => (
        <li key={k} className={i < at ? "is-done" : i === at ? "is-now" : ""}>{label}</li>
      ))}
    </ol>
  );
}

function SpecView({ spec }) {
  return (
    <div className="lb-mk-spec">
      <h4>{spec.title}</h4>
      {spec.summary && <p>{spec.summary}</p>}
      {spec.feasible !== "ok" && (
        <p className="lb-mk-warn">일부 기능은 지금 설계로 만들 수 없습니다 — 아래 '빠진 것'을 확인해 주세요.</p>
      )}
      <h5>기능</h5>
      <ol>{(spec.features || []).map((f, i) => <li key={i}><b>{f.name}</b> — {f.when} → {f.does}</li>)}</ol>
      {spec.interface?.length > 0 && (<><h5>사용 방법</h5><ul>{spec.interface.map((x, i) => <li key={i}>{x}</li>)}</ul></>)}
      {spec.settings?.length > 0 && (
        <>
          <h5>설정값</h5>
          <table className="lb-mk-table">
            <tbody>{spec.settings.map((s, i) => <tr key={i}><th>{s.name}</th><td>{s.value}</td><td>{s.note}</td></tr>)}</tbody>
          </table>
        </>
      )}
      {spec.network && spec.network.kind !== "none" && (
        <><h5>무선</h5><p>{spec.network.kind === "wifi" ? "와이파이" : "블루투스"} — {spec.network.note}</p></>
      )}
      {spec.acceptance?.length > 0 && (<><h5>완료 기준 (조립 후 확인)</h5><ul className="lb-mk-check">{spec.acceptance.map((x, i) => <li key={i}>{x}</li>)}</ul></>)}
      {spec.assumptions?.length > 0 && (<><h5>정해 둔 기본값</h5><ul>{spec.assumptions.map((x, i) => <li key={i}>{x}</li>)}</ul></>)}
      {spec.out_of_scope?.length > 0 && (<><h5>이번 범위에서 빠진 것</h5><ul>{spec.out_of_scope.map((x, i) => <li key={i}>{x}</li>)}</ul></>)}
    </div>
  );
}

/* 승인 뒤 빌드 상태 — 빌드 서버 · 검수 과정은 감추고 상태 문구(BuildView.label) · 주문 번호만 보인다 */
function BuildStatus({ build }) {
  const st = build.build_status;
  const no = <span className="lb-mk-log__cost">빌드 주문 {build.order_no} · {fmtDate(build.created_at, true)}</span>;
  if (build.active) {
    return (
      <div className="lb-mk-log" aria-live="polite">
        <b className="lb-mk-log__now">{build.label}</b>
        <span>빌드 서버가 승인한 명세 · 배선대로 펌웨어를 만들고, 담당자가 검수한 뒤 이 화면에서 받을 수 있습니다. 완성되면 이 화면이 저절로 바뀝니다.</span>
        {no}
      </div>
    );
  }
  if (st === "failed") {
    return <p className="lb-mk-warn">{build.label} <span className="lb-mk-log__cost">· 빌드 주문 {build.order_no}</span></p>;
  }
  if (st === "cancelled") {
    return (
      <p className="lb-mk-warn">
        {build.label} — <a href="/contact">문의하기</a> <span className="lb-mk-log__cost">· 빌드 주문 {build.order_no}</span>
      </p>
    );
  }
  return null;
}

/* 브라우저로 플래시 — ESP Web Tools (크롬 · 엣지의 Web Serial). manifest 는 서명 링크(파트도 서명 링크로 바뀌어 있다) */
function WebFlash({ manifest }) {
  useEffect(() => {
    if (window.customElements?.get("esp-web-install-button") || document.querySelector(`script[src="${ESP_WEB_TOOLS}"]`)) return;
    const s = document.createElement("script");
    s.type = "module";
    s.src = ESP_WEB_TOOLS;
    document.head.appendChild(s);
  }, []);
  return (
    <esp-web-install-button manifest={manifest}>
      <button slot="activate" type="button" className="lb-btn lb-btn--sm lb-btn--primary">브라우저로 플래시</button>
      <span slot="unsupported" className="lb-mk-note">PC 의 크롬 · 엣지에서 열면 바로 올릴 수 있습니다.</span>
      <span slot="not-allowed" className="lb-mk-note">https 주소에서만 쓸 수 있습니다.</span>
    </esp-web-install-button>
  );
}

async function firmwareLinks(pid) {
  const res = await fetch(`/api/projects/${pid}/firmware`, { credentials: "same-origin" });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "펌웨어 링크를 받지 못했습니다.");
  return { ...d, at: Date.now() };
}

/* 결과물 — 펌웨어가 완성(done)되면 본인이 받는다. 펌웨어 · 소스는 24시간 서명 링크라 오래 열어 둔 화면에서는
   누를 때 새로 받아 오고, 플래시 버튼의 매니페스트는 5분마다 · 화면으로 돌아올 때 받은 시각을 보고 바꾼다 */
function Results({ pid, done }) {
  const [fw, setFw] = useState(null);
  const [error, setError] = useState("");
  const fwRef = useRef(null);
  const webSerial = typeof navigator !== "undefined" && "serial" in navigator;

  const refresh = useCallback(async (force) => {
    const cur = fwRef.current;
    if (!force && cur && Date.now() - cur.at < LINK_FRESH_MS) return cur;
    try {
      const d = await firmwareLinks(pid);
      fwRef.current = d;
      setFw(d);
      setError("");
      return d;
    } catch (e) {
      setError(e.message);
      return null;
    }
  }, [pid]);

  useEffect(() => { refresh(true); }, [refresh]);
  useEffect(() => {
    const check = () => { if (document.visibilityState === "visible") refresh(false); };
    const t = setInterval(check, 5 * 60 * 1000);
    document.addEventListener("visibilitychange", check);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", check); };
  }, [refresh]);

  /* 서명 링크 버튼 — 받은 지 오래되었으면 링크를 새로 받아 그 주소로 연다 */
  const signed = (key, label, primary) => fw?.[key] && (
    <a className={`lb-btn lb-btn--sm${primary ? " lb-btn--primary" : ""}`} href={fw[key]}
       onClick={async (e) => {
         if (fwRef.current && Date.now() - fwRef.current.at < LINK_FRESH_MS) return;
         e.preventDefault();
         const d = await refresh(true);
         if (d?.[key]) window.location.assign(d[key]);
       }}>
      {label}
    </a>
  );

  return (
    <>
      {error && <p className="lb-error">{error}</p>}
      {done.flash && (webSerial
        ? fw && <div className="lb-mk-acts"><WebFlash key={fw.manifest_url} manifest={fw.manifest_url} /></div>
        : <p className="lb-mk-note">PC 의 크롬 · 엣지에서 열면 보드를 USB 로 연결해 브라우저에서 바로 올릴 수 있습니다.</p>)}
      <div className="lb-mk-acts">
        {signed("firmware_zip_url", "펌웨어 다운로드", !done.flash || !webSerial)}
        {signed("source_url", "소스 코드 받기")}
        <a className="lb-btn lb-btn--sm" href={`/api/laona/projects/${pid}/package.zip`}>결과물 ZIP 받기</a>
        <a className="lb-btn lb-btn--sm" href={`/api/laona/projects/${pid}/wiring.pdf`} target="_blank" rel="noreferrer">배선도 PDF</a>
      </div>
      <p className="lb-mk-note">
        {done.flash && webSerial && "보드를 USB 로 PC 에 연결하고 [브라우저로 플래시]를 누르면 바로 올라갑니다. "}
        펌웨어 다운로드에는 bin 파일과 올리는 방법(README)이, 결과물 ZIP 에는 펌웨어 · 소스 · 기능 명세 · 배선도 PDF · 부품 목록이
        들어 있습니다. 펌웨어 · 소스 링크는 24시간 동안 쓸 수 있고, 이 화면에서 누르면 새로 받아 옵니다.
      </p>
    </>
  );
}

function OrderForm({ p, quote, onOrdered }) {
  const [f, setF] = useState({
    name: p.member?.name || "", phone: "", email: p.member?.email || "", zip: "", address: "", address2: "",
    memo: "", depositor: p.depositor || p.member?.name || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await projectApi(`/${p.id}/order`, { method: "POST", body: JSON.stringify(f) });
      onOrdered();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const field = (k, label, props = {}) => (
    <label className="lb-field"><span>{label}</span><input value={f[k]} onChange={set(k)} {...props} /></label>
  );
  return (
    <form className="lb-mk-form" onSubmit={submit}>
      {field("name", "받는 분", { required: true, maxLength: 60, autoComplete: "name" })}
      {field("phone", "연락처", { required: true, maxLength: 40, autoComplete: "tel", inputMode: "tel" })}
      {field("email", "이메일", { required: true, maxLength: 200, type: "email", autoComplete: "email" })}
      <div className="lb-field2">
        {field("zip", "우편번호", { maxLength: 20, autoComplete: "postal-code" })}
        {field("depositor", "입금자명", { required: true, maxLength: 60 })}
      </div>
      {field("address", "주소", { required: true, maxLength: 300, autoComplete: "address-line1" })}
      {field("address2", "상세 주소", { maxLength: 300, autoComplete: "address-line2" })}
      {field("memo", "배송 메모", { maxLength: 400 })}
      {error && <p className="lb-error">{error}</p>}
      <button type="submit" className="lb-btn lb-btn--primary" disabled={busy || quote.unpriced.length > 0}>
        {busy ? "주문하는 중…" : `${won(quote.total)} 결산 주문하기`}
      </button>
    </form>
  );
}

function Quote({ quote }) {
  return (
    <table className="lb-mk-table lb-mk-quote">
      <tbody>
        {quote.lines.map((ln) => (
          <tr key={ln.slug}><th>{ln.name}{ln.qty > 1 ? ` × ${ln.qty}` : ""}</th><td>{won(ln.price * ln.qty)}</td></tr>
        ))}
        <tr><th>배송비</th><td>{quote.shipping ? won(quote.shipping) : "무료"}</td></tr>
        <tr className="lb-mk-quote__sum"><th>결제할 금액</th><td>{won(quote.total)}</td></tr>
      </tbody>
    </table>
  );
}

export default function MakeTab({ p, onUsage, onStatus, reload }) {
  const [prod, setProd] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await projectApi(`/${p.id}/production`);
      setProd(d);
      onStatus(d.status);
      if (d.usage) onUsage(d.usage);
    } catch (e) {
      setError(e.message);
    }
  }, [p.id, onStatus, onUsage]);

  useEffect(() => { load(); }, [load]);
  /* 주문 입금 대기 중에는 30초마다 상태를 따라간다 */
  const waitingPay = prod?.status === "ORDERED";
  useEffect(() => {
    if (!waitingPay) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [waitingPay, load]);

  async function act(key, path, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(key);
    setError("");
    try {
      const d = await projectApi(`/${p.id}/${path}`, { method: "POST" });
      if (d.usage) onUsage(d.usage);
      await load();
    } catch (e) {
      setError(e.message);
      /* 402 — 무료 한도 · 이용료: 상태를 다시 읽어 결제 / 직접 만들기 안내를 띄운다 */
      if (e.status === 402) { await load(); if (reload) await reload(); }
    } finally {
      setBusy("");
    }
  }

  if (!prod) {
    return <div className="lb-win__pad">{error ? <p className="lb-error">{error}</p> : <p className="lb-mk-note">불러오는 중…</p>}</div>;
  }
  const s = prod.status;
  const designing = s === "ACTIVE" || s === "DESIGNED";
  const hasModules = s !== "ACTIVE";
  const done = prod.done;          // 예전 자동 빌드로 완성된 펌웨어
  const legacy = s === "BUILT" || !!done;
  const fw = prod.fw;
  const paid = ["PAID", "SHIPPED", "DELIVERED"].includes(s);
  const plan = prod.plan || {};
  const needPlan = !p.admin && !plan.paid && designing && plan.over;
  /* 설계 다시 열기 — 저장했지만 아직 주문하지 않았을 때 */
  const canReopen = s === "APPROVED" && prod.ai && (!fw || fw.status === "REQUESTED");
  const refresh = async () => { await load(); if (reload) await reload(); };
  const saveText = "이 설계를 내 프로젝트로 저장할까요?\n저장하면 대화 · 모듈 바꾸기가 잠기고, 기능 명세가 펌웨어 기능정의서가 됩니다.\n"
    + "내 프로젝트에서 [주문하기]로 부품과 펌웨어 개발 · 빌드를 함께 주문할 수 있습니다.";

  return (
    <div className="lb-win__pad lb-win__scroll">
      <Stepper prod={prod} />
      {!prod.ai && (
        <p className="lb-mk-warn">라오나봇 AI 설계 서비스는 지금 준비 중입니다. 명세 정리 · 저장은 서비스가 열리면 이어서 할 수 있습니다.</p>
      )}
      {error && <p className="lb-error">{error}</p>}

      {/* 1. 기능 명세 — 펌웨어 기능정의서가 된다 */}
      <section className="lb-dsec">
        <h3>기능 명세</h3>
        {!prod.spec && (
          <p className="lb-mk-note">
            대화로 정한 기능을 펌웨어가 따를 명세로 정리합니다. 명세를 확인하고 [내 프로젝트로 저장]을 누르면 이 명세가
            펌웨어 기능정의서가 됩니다. 기능정의서에 적힌 기능만 구현되므로, 대화로 기능을 자세히 정해 주세요.
          </p>
        )}
        {prod.spec && (designing ? <SpecView spec={prod.spec} /> : (
          <details className="lb-mk-fold">
            <summary>저장한 명세 보기 — {prod.spec.title}</summary>
            <SpecView spec={prod.spec} />
          </details>
        ))}
        {prod.spec_stale && designing && (
          <p className="lb-mk-warn">명세를 만든 뒤 설계(모듈 · 배선)가 바뀌었습니다. 다시 정리해 주세요.</p>
        )}
        {needPlan && <PlanPanel p={p} plan={plan} bank={prod.bank} onChanged={refresh} />}
        {designing && prod.ai && (
          <div className="lb-mk-acts">
            <button type="button" className="lb-btn lb-btn--sm" disabled={!!busy || !hasModules || (plan.over && !p.admin)}
                    onClick={() => act("spec", "spec")}>
              {busy === "spec" ? "정리하는 중… (1분 정도)" : prod.spec ? "명세 다시 정리" : "기능 명세 정리하기"}
            </button>
            {prod.spec && !prod.spec_stale && (
              <button type="button" className="lb-btn lb-btn--sm lb-btn--primary" disabled={!!busy}
                      onClick={() => act("approve", "approve", saveText)}>
                {busy === "approve" ? "저장하는 중…" : "내 프로젝트로 저장"}
              </button>
            )}
          </div>
        )}
        {!hasModules && <p className="lb-mk-note">먼저 [대화] 탭에서 모듈을 적용해 주세요.</p>}
        {designing && !plan.paid && <p className="lb-mk-note">명세 정리에도 사용량이 듭니다 (보통 수백 원) — 무료 한도에서 빠집니다.</p>}
      </section>

      {/* 2. 저장 · 주문 — 내 프로젝트에서 부품과 펌웨어 개발 · 빌드를 함께 주문한다 */}
      {!designing && !legacy && (
        <section className="lb-dsec">
          <h3>주문 · 펌웨어 개발</h3>
          {fw && (
            <p className="lb-mk-note">
              <span className={`lb-tag lb-tag--st lb-tag--${FW_STATUS_TONE[fw.status] || ""}`}>펌웨어 {fw.label}</span>{" "}
              의뢰번호 {fw.request_no}
            </p>
          )}
          {s === "APPROVED" && (
            <>
              <p className="lb-mk-note">
                내 프로젝트에 저장했습니다. <b>내 프로젝트 › 라오나봇 프로젝트</b>에서 [주문하기]를 누르면 부품과
                펌웨어 개발 · 빌드가 함께 주문됩니다.
              </p>
              <FwTerms fee={prod.fw_fee} />
              <div className="lb-mk-acts">
                <Link to="/laonabot/projects" className="lb-btn lb-btn--sm lb-btn--primary">내 프로젝트에서 주문하기</Link>
                {canReopen && (
                  <button type="button" className="lb-btn lb-btn--sm" disabled={!!busy}
                          onClick={() => act("reopen", "reopen", "설계를 다시 열까요?\n대화 · 모듈 바꾸기를 다시 할 수 있고, 다시 저장하면 펌웨어 개발의뢰도 새 내용으로 바뀝니다.")}>
                    설계 다시 열기
                  </button>
                )}
              </div>
            </>
          )}
          {s === "ORDERED" && prod.order && (
            <div className="lb-deposit__bank">
              <span>주문 #{prod.order.order_no} · 입금 대기</span>
              <b>{prod.bank.bank} {prod.bank.account}</b>
              <span>예금주 {prod.bank.holder}</span>
              <span>금액 <b>{won(prod.order.total)}</b> · 입금자명 {prod.order.depositor}</span>
            </div>
          )}
          {paid && prod.order && (
            <p className="lb-mk-note">
              주문 #{prod.order.order_no} · {s === "PAID" ? "입금 확인 — 펌웨어를 개발하고 키트를 준비하고 있습니다" : s === "SHIPPED" ? `발송됨 ${prod.order.tracking || ""}` : "배송 완료"}
            </p>
          )}
        </section>
      )}

      {/* 예전 자동 빌드로 진행한 프로젝트 — 펌웨어 · 결산 · 결과물 */}
      {legacy && prod.build && (prod.build.build_status !== "done" || prod.build.id !== done?.id) && (
        <section className="lb-dsec"><h3>펌웨어</h3><BuildStatus build={prod.build} /></section>
      )}
      {legacy && prod.quote && s === "BUILT" && (
        <section className="lb-dsec">
          <h3>결산</h3>
          <Quote quote={prod.quote} />
          {prod.quote.unpriced.length > 0 ? (
            <p className="lb-mk-warn">
              가격이 정해지지 않은 부품이 있어 아직 주문할 수 없습니다: {prod.quote.unpriced.join(", ")}.
            </p>
          ) : (
            <>
              <p className="lb-mk-note">주문하면 아래 계좌로 입금해 주세요. 입금이 확인되면 키트를 보내 드립니다.</p>
              <OrderForm p={p} quote={prod.quote} onOrdered={load} />
            </>
          )}
        </section>
      )}
      {legacy && s === "ORDERED" && prod.order && (
        <section className="lb-dsec">
          <h3>결산</h3>
          <div className="lb-deposit__bank">
            <span>주문 #{prod.order.order_no} · 입금 대기</span>
            <b>{prod.bank.bank} {prod.bank.account}</b>
            <span>예금주 {prod.bank.holder}</span>
            <span>금액 <b>{won(prod.order.total)}</b> · 입금자명 {prod.order.depositor}</span>
          </div>
        </section>
      )}
      {done && (
        <section className="lb-dsec">
          <h3>결과물</h3>
          {paid && prod.order && (
            <p className="lb-mk-note">
              주문 #{prod.order.order_no} · {s === "PAID" ? "입금 확인 — 키트를 준비하고 있습니다" : s === "SHIPPED" ? `발송됨 ${prod.order.tracking || ""}` : "배송 완료"}
            </p>
          )}
          <Results key={done.id} pid={p.id} done={done} />
        </section>
      )}
    </div>
  );
}
